import React from 'react'
import styled from 'styled-components'
import { Box, Flex, Icon, Loading } from '../../atoms'
import { PriceDistributionChart } from '../PriceDistributionChart'
import { PriceByPropertyTypeChart } from '../PriceByPropertyTypeChart'
import { YearlyMarketVolumeChart } from '../YearlyMarketVolumeChart'
import {
  PriceDynamicsChartProps,
  DEFAULT_PRICE_DYNAMICS_CHART_LABELS,
} from './PriceDynamicsChart.types'
import { formatCurrency, formatChange } from './PriceDynamicsChart.utils'

const DashboardContainer = styled.div`
  width: 100%;
`

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`

const MetricCard = styled(Box)`
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  position: relative;
  overflow: hidden;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transition: box-shadow 0.3s ease;
  }
`

const MetricValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 8px 0;
`

const MetricLabel = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 4px;
`

const MetricChange = styled.div<{ $isPositive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $isPositive, theme }) =>
    $isPositive ? theme.colors.success : theme.colors.danger};
  margin-top: 8px;
`

const SparklineContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  opacity: 0.1;
`

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
`

const ChartContainer = styled(Box)`
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  min-height: 400px;
  display: flex;
  flex-direction: column;
`

export const PriceDynamicsChart: React.FC<PriceDynamicsChartProps> = ({
  data,
  loading = false,
  error = null,
  labels = {},
}) => {
  const mergedLabels = { ...DEFAULT_PRICE_DYNAMICS_CHART_LABELS, ...labels }

  if (loading) {
    return (
      <DashboardContainer>
        <Flex justify="center" align="center" style={{ minHeight: '400px' }}>
          <Loading size="large" />
        </Flex>
      </DashboardContainer>
    )
  }

  if (error || !data) {
    return (
      <DashboardContainer>
        <Flex
          justify="center"
          align="center"
          style={{ minHeight: '400px', color: '#999' }}
        >
          {error || mergedLabels.noData}
        </Flex>
      </DashboardContainer>
    )
  }

  return (
    <DashboardContainer>
      <MetricsGrid>
        <MetricCard>
          <MetricLabel>{mergedLabels.avgTransactionPrice}</MetricLabel>
          <MetricValue>{formatCurrency(data.avgTransactionPrice)}</MetricValue>
          <MetricChange $isPositive={data.avgTransactionPriceChange >= 0}>
            <Icon
              name={
                data.avgTransactionPriceChange >= 0
                  ? 'trending-up'
                  : 'trending-down'
              }
              size="small"
            />
            {formatChange(data.avgTransactionPriceChange)} {mergedLabels.yoy}
          </MetricChange>
          <SparklineContainer>
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 100 40"
              preserveAspectRatio="none"
            >
              <polyline
                points={data.sparklineData
                  .map(
                    (v, i) =>
                      `${(i * 100) / (data.sparklineData.length - 1)},${40 - v * 0.4}`
                  )
                  .join(' ')}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </SparklineContainer>
        </MetricCard>

        <MetricCard>
          <MetricLabel>{mergedLabels.totalMarketVolume}</MetricLabel>
          <MetricValue>{formatCurrency(data.totalMarketVolume)}</MetricValue>
          <MetricChange $isPositive={data.totalMarketVolumeChange >= 0}>
            <Icon
              name={
                data.totalMarketVolumeChange >= 0
                  ? 'trending-up'
                  : 'trending-down'
              }
              size="small"
            />
            {formatChange(data.totalMarketVolumeChange)} {mergedLabels.yoy}
          </MetricChange>
        </MetricCard>

        <MetricCard>
          <MetricLabel>{mergedLabels.priceVelocityIndex}</MetricLabel>
          <MetricValue>{data.priceVelocityIndex}</MetricValue>
          <MetricChange $isPositive={data.priceVelocityIndexChange >= 0}>
            <Icon
              name={
                data.priceVelocityIndexChange >= 0
                  ? 'trending-up'
                  : 'trending-down'
              }
              size="small"
            />
            {formatChange(data.priceVelocityIndexChange)} {mergedLabels.mom}
          </MetricChange>
        </MetricCard>
      </MetricsGrid>

      <ChartsGrid>
        <ChartContainer>
          <PriceDistributionChart
            data={data.priceDistribution}
            loading={false}
            error={null}
            title={mergedLabels.priceDistribution!}
            formatCurrency={formatCurrency}
          />
        </ChartContainer>

        <ChartContainer>
          <PriceByPropertyTypeChart
            data={data.pricePerM2ByType}
            loading={false}
            error={null}
            title={mergedLabels.priceByPropertyType!}
            formatCurrency={formatCurrency}
            orientation="horizontal"
          />
        </ChartContainer>

        {data.yearlyMarketVolume && data.yearlyMarketVolume.length > 0 && (
          <ChartContainer>
            <YearlyMarketVolumeChart
              data={data.yearlyMarketVolume}
              loading={false}
              error={null}
              title={mergedLabels.yearlyMarketVolume!}
              height={350}
            />
          </ChartContainer>
        )}
      </ChartsGrid>
    </DashboardContainer>
  )
}
